import { SagaIterator } from 'redux-saga';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';
import {
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  convertToBffLaufzeit,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import {
  Fachbaustein,
  Feature,
  SpartenType,
  TkvDatenAntrag,
  VorschadenArt,
} from '@ps20/bff-models';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { mapSublimit, mapTierAntrag } from './transformer/tkvData.transformer';

export const HB_UND_VORSORGESCHUTZ = 'TM000053';
export const OPERATIONEN = 'TM000059';

export function* watchVorschlagdatenChangend(): SagaIterator {
  yield takeEvery(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.TKV));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>): SagaIterator {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const angebote = action.payload.angebotsIds;

  if (
    !angebote.some(angebot => angebot.appId === App.TKV && angebot.angebotsId === state.angebotsId)
  ) {
    return yield cancel();
  }

  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const sublimit: number = mapSublimit(ausgewaehlteProduktlinie.zusatzoptionen[0].eigenschaft);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

  const vorschlagdaten: TkvDatenAntrag = {
    spartenType: SpartenType.TIERKRANKEN,
    jahresbeitrag: state.jahresbeitrag,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
    gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
      state.vertragsoptionen.zahlungsweise,
      state.gesamtbeitragMitRabatt,
      state.jahresbeitrag,
      App.TKV,
    ),
    savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
    angebotsId: state.angebotsId,
    antragsId: state.antragsnummer,
    sicherungsglaeubiger: [],
    hspLfnr: state.produkt.hspLfnr,
    vorschaedenVorhanden: state.versicherungsnehmer.isBehandelt,
    vorschaeden: [
      {
        vorschadenArt: VorschadenArt.TKV_HB,
        anzahl: state.versicherungsnehmer.behandlungen.ohneOperation ? 999 : 0,
        betrachtungszeitraum: 5,
      },
      {
        vorschadenArt: VorschadenArt.TKV_OP,
        anzahl: state.versicherungsnehmer.anzahlOperationen,
        betrachtungszeitraum: 5,
      },
    ],
    hasHeilbehandlungen: state.versicherungsnehmer.behandlungen.ohneOperation,
    anzVorschaedenOp: state.versicherungsnehmer.anzahlOperationen,
    sublimit: sublimit,
    tierAntrag: mapTierAntrag(state.versicherungsnehmer),
    produkt: {
      beginn: null,
      ablauf: null,
      selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
      produktline: ausgewaehlteProduktlinie.produktName,
      kurztext: ausgewaehlteProduktlinie.kurztext,
      langtext: ausgewaehlteProduktlinie.langtext,
      fachschluessel: setFachschluessel(state, sublimit, ausgewaehlteProduktlinie),
    },
    rbdMax: ausgewaehlteProduktlinie.rbdMax,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    savedRbdNachlass: state.taasInfo.savedRbdNachlass,
    savedTbNachlass: state.taasInfo.savedTbNachlass,
    vorteilsrabatt: state.taasInfo.vorteilsrabatt,
    preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
    vorversicherung: [],
    antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
    laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
    rabattstufe: state.rabattstufe,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    zielmarktAbweichung: false,
    kooperation: state.vertragsoptionen.selectedKooperation,
  };

  handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);
  yield call(globalPut.put, new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten));
}

export function setFachschluessel(
  state: IAppState,
  sublimit: number,
  ausgewaehlteProduktlinie: Produktlinie,
): Array<Fachbaustein> {
  const fachschluessel = getFachschluessel(state.produkt);
  fachschluessel.forEach(fschl => {
    switch (fschl.fachschluessel) {
      case HB_UND_VORSORGESCHUTZ: {
        fschl.summe = sublimit.toString();
        const hbZusatzoption = ausgewaehlteProduktlinie.zusatzoptionen.find(
          zusatzoption => zusatzoption.name === 'HB',
        );

        fschl.deltafaktor = hbZusatzoption.deltafaktor;
        fschl.anzahlVorschaeden = hbZusatzoption.anzahlVorschaeden;
        fschl.plzFaktor = hbZusatzoption.plzFaktor;
        break;
      }
      case OPERATIONEN: {
        const opGefahr = ausgewaehlteProduktlinie.gefahren.find(gefahr => gefahr.name === 'OP');

        fschl.deltafaktor = opGefahr.deltafaktor;
        fschl.anzahlVorschaeden = opGefahr.anzahlVorschaeden;
        fschl.plzFaktor = opGefahr.plzFaktor;
        break;
      }
    }
  });

  return fachschluessel;
}
